/**
 * upgradeMoment v1.4 (2026-10-23, T972: a request may carry `snapshot` (the cheaper action the balance covers,
 *   offered first) and `reportOrder` (this one Full Report, paid once inside the dialog); the plan becomes the
 *   second option there. A 10-15 trial signup from /funding hit the plan-only dialog on its first click and left.)
 * upgradeMoment v1.3 (2026-09-29, T290: a subscriber's plan button is "Switch to <plan>" and opens
 *   the plan switch (planSwitch.ts), never a second subscription; a stale "plan" offer that the
 *   server answers 409 already_subscribed is turned into the switch too)
 * upgradeMoment v1.2 (2026-09-29, T240: limit type "monitor" for the first-scan Watch it daily card)
 * upgradeMoment v1.1 (2026-09-28, T194/T195: scan cap -> first paid plan, server sentence, SCAN_CAP_TIP)
 * upgradeMoment v1.0 (2026-09-27, cc_tasks row 82)
 *
 * EVERY PLACE A FREE OR TRIAL USER IS BLOCKED IS AN UPGRADE MOMENT, NOT A DEAD
 * END. A call site that hits a limit calls openUpgradeMoment({...}) with what
 * the user was trying to do and what it needs; <UpgradeMomentHost/> (mounted
 * once in App.tsx) renders the one shared dialog, picks the right thing to buy,
 * opens Checkout in a new tab, and remembers the action so the page can re-run
 * it once the payment has landed (useUpgradeResume).
 *
 * WHAT IS OFFERED, AND WHY IT IS NOT ALWAYS A PACK. Pack credits are spendable
 * only on a paid plan (tier_credit_grants.purchased_spendable is false for free
 * and trial). Selling a free user a pack would sell them credits they cannot
 * spend. So:
 *   - free / trial, short of credits  -> the cheapest plan whose monthly grant
 *                                        covers the need (Pro unless it cannot)
 *   - paid plan, short of credits     -> the cheapest pack that covers the
 *                                        shortfall (the largest if none does)
 *   - a feature on a higher plan      -> that plan; a user who already pays for
 *                                        a plan changes it in the billing portal
 *                                        so they never hold two subscriptions
 * Prices and credit counts come from credit_pack_skus and the generated
 * planPricing.ts (plan_definitions). Nothing here names a price.
 *
 * THE RESUME RECORD. Written to localStorage (mf_resume_action, 24 h) before
 * Checkout opens, and sent to create-checkout, which stores the same fields as
 * Checkout metadata (resume_action, resume_ref, limit_type) so the record is
 * verifiable against Stripe. It never carries a price.
 */
import { supabase } from "@/integrations/supabase/client";
import { adsBeginCheckout } from "@/lib/adsConversion";
import { planUrl, switchPlanUrl } from "@/lib/planSwitch";
import { PLANS, PLAN_LADDER, planFor, tierAtLeast, type PlanRecord, type PlanSlug } from "@/lib/planPricing";
import { acquisition } from "@/lib/acquisition";

export const SEE_ALL_PLANS_URL = "https://mentionfox.com/pricing";
/** The first rung above free on the generated ladder (plan_definitions). */
const FIRST_PAID_PLAN: PlanSlug = PLAN_LADDER[1];
const RESUME_KEY = "mf_resume_action";
const RESUME_TTL_MS = 24 * 60 * 60 * 1000;

/** What kind of limit was hit. Also the funnel step suffix (limit_hit:<type>). */
export type LimitType =
  | "credits" | "scan" | "csv" | "report" | "follow" | "dossier" | "plan"
  | "seats" | "clients" | "den" | "enrich" | "sequence" | "geo" | "export"
  /** T240: "Watch it daily" on a first scan (daily brand monitor). */
  | "monitor";

export interface UpgradeNeed {
  /** Credits the action needs. */
  credits?: number;
  /** Credits the user has now, when the caller already knows. */
  have?: number;
  /** The plan that unlocks the action. */
  plan?: PlanSlug;
  /** One plain sentence for a plan gate, e.g. "Scans on more platforms are on Pro". */
  reason?: string;
  /** Out of credits and the exact cost is not known to the caller. */
  out?: boolean;
  /** A cap on the current plan: the answer is the next plan up. */
  above?: boolean;
  /** Hover text on the explanation line, e.g. how the scan cap works (T194). */
  tip?: string;
}

export interface ResumeSpec {
  /** Short action key the page's resume hook listens for, e.g. "scan", "csv", "report". */
  action: string;
  /** What to re-run: a scan config id, a slug, a report id, or a short token. */
  ref?: string;
}

export interface UpgradeMomentRequest {
  limitType: LimitType;
  /** What the user was trying to do, in their words: "Run this scan". */
  attempting: string;
  need: UpgradeNeed;
  resume?: ResumeSpec;
  /** Defaults to the current path + query. */
  returnTo?: string;
  /**
   * v1.4 (T972): the cheaper thing the user can do right now with what they hold (the Snapshot on the company
   * they picked). Shown first, as the primary button, when the balance covers `credits`.
   */
  snapshot?: { label: string; credits: number; run: () => void };
  /**
   * v1.4 (T972): this one Full Report, paid once at checkout inside the dialog (run-service report_order create
   * with embedded: true, the T962 checkout), no plan change. Shown before the plan offer.
   */
  reportOrder?: { entityName: string; entityDomain?: string | null; pagePath?: string };
}

export interface ResumeRecord {
  action: string;
  ref: string | null;
  limitType: LimitType;
  returnTo: string;
  need: { credits?: number; plan?: PlanSlug };
  at: number;
}

// ── the global opener ────────────────────────────────────────────────
const OPEN_EVENT = "mf:upgrade-moment";

/** Open the shared upgrade dialog. Safe to call from any catch block. */
export function openUpgradeMoment(req: UpgradeMomentRequest): void {
  try {
    window.dispatchEvent(new CustomEvent<UpgradeMomentRequest>(OPEN_EVENT, { detail: req }));
  } catch { /* no window: nothing to show */ }
}
export function onUpgradeMomentRequest(fn: (r: UpgradeMomentRequest) => void): () => void {
  const h = (e: Event) => fn((e as CustomEvent<UpgradeMomentRequest>).detail);
  window.addEventListener(OPEN_EVENT, h);
  return () => window.removeEventListener(OPEN_EVENT, h);
}

/** Current path + query, which is where Checkout brings the buyer back to. */
export function currentReturnTo(): string {
  const q = new URLSearchParams(window.location.search);
  for (const k of ["pack", "session_id", "upgrade", "success"]) q.delete(k);
  const rest = q.toString();
  return window.location.pathname + (rest ? `?${rest}` : "");
}

// ── the resume record ────────────────────────────────────────────────
export function saveResume(r: Omit<ResumeRecord, "at">): void {
  try { localStorage.setItem(RESUME_KEY, JSON.stringify({ ...r, at: Date.now() })); } catch { /* storage off */ }
}
export function peekResume(): ResumeRecord | null {
  try {
    const raw = localStorage.getItem(RESUME_KEY);
    if (!raw) return null;
    const r = JSON.parse(raw) as ResumeRecord;
    if (!r || typeof r.at !== "number" || Date.now() - r.at > RESUME_TTL_MS) {
      localStorage.removeItem(RESUME_KEY);
      return null;
    }
    return r;
  } catch { return null; }
}
/**
 * Take the record, once. Two tabs can be open on the page (the one the buyer
 * started in and the one Checkout returned to); whichever claims first re-runs
 * the action and the other finds nothing, so an action never runs twice.
 */
export function claimResume(action: string): ResumeRecord | null {
  const r = peekResume();
  if (!r || r.action !== action) return null;
  try {
    const raw = localStorage.getItem(RESUME_KEY);
    if (!raw || JSON.parse(raw).at !== r.at) return null;
    localStorage.removeItem(RESUME_KEY);
  } catch { return null; }
  return r;
}
export function clearResume(): void {
  try { localStorage.removeItem(RESUME_KEY); } catch { /* ignore */ }
}

// ── tracking (feeds refresh_funnel_daily) ───────────────────────────
function evtSession(): string | null {
  try {
    const k = "mf_evt_session";
    let v = localStorage.getItem(k);
    if (!v) { v = Date.now().toString(36) + Math.random().toString(36).slice(2, 10); localStorage.setItem(k, v); }
    return v;
  } catch { return null; }
}
function optedOut(): boolean {
  try {
    const n = navigator as any;
    return n.doNotTrack === "1" || (window as any).doNotTrack === "1" || n.globalPrivacyControl === true || n.webdriver === true;
  } catch { return false; }
}
/**
 * limit_hit and upgrade_click are recorded from the browser; checkout_started is
 * recorded by create-checkout and paid by the Stripe webhook, so the two steps
 * that involve money are written by the server that saw them.
 */
export function trackUpgradeEvent(limitType: LimitType, action: "limit_hit" | "upgrade_click"): void {
  if (optedOut()) return;
  void supabase.functions.invoke("run-service/page_event", {
    body: {
      page_url: "https://mentionfox.com" + (window.location.pathname.replace(/\/$/, "") || "/"),
      element: `upgrade:${limitType}`,
      action,
      session_id: evtSession(),
      acq: acquisition(),
    },
  }).catch(() => { /* tracking never blocks the user */ });
}

// ── what to offer ────────────────────────────────────────────────────
export interface PackRow { sku: string; credit_count: number; price_usd_cents: number; display_name: string }

export type UpgradeOffer =
  | { kind: "pack"; pack: PackRow; priceLabel: string }
  | { kind: "plan"; plan: PlanRecord }
  | { kind: "portal"; plan: PlanRecord };

export interface BalanceSnapshot {
  tier: string;
  spendable: number;
  purchased: number;
  purchasedFrozen: boolean;
}

export async function readBalance(): Promise<BalanceSnapshot | null> {
  const { data, error } = await supabase.rpc("get_my_credit_balance" as any);
  if (error) return null; // unknown balance: the dialog still opens, the offer falls back to the first paid plan
  const d = data as any;
  if (!d || d.ok !== true) return null;
  return {
    tier: String(d.tier || "free"),
    spendable: Number(d.spendable_total || 0),
    purchased: Number(d.purchased || 0),
    purchasedFrozen: d.purchased_frozen === true,
  };
}

let packCache: PackRow[] | null = null;
export async function readPacks(): Promise<PackRow[]> {
  if (packCache) return packCache;
  const { data, error } = await supabase.from("credit_pack_skus")
    .select("sku, credit_count, price_usd_cents, display_name")
    .not("stripe_price_id", "is", null)
    .like("sku", "pack_%") // T425: full_report_single is not a top-up pack
    .order("credit_count", { ascending: true });
  // Unreadable pack list: offer no pack rather than a guessed one; the dialog
  // then points at "See all plans".
  if (error) return [];
  packCache = ((data as any[]) || []) as PackRow[];
  return packCache;
}

export function formatCents(c: number): string {
  const d = c / 100;
  return "$" + (Number.isInteger(d) ? d.toLocaleString("en-US") : d.toFixed(2));
}

/** A tier that pays for a plan (and so can spend pack credits). */
function isPaidTier(tier: string): boolean {
  return tierAtLeast(tier, "pro") && !["free", "trial", "lite", ""].includes(String(tier).toLowerCase());
}

export function pickOffer(need: UpgradeNeed, bal: BalanceSnapshot | null, packs: PackRow[]): UpgradeOffer | null {
  const tier = bal?.tier || "free";
  const paid = isPaidTier(tier);
  const higher = (min: PlanSlug): PlanRecord => {
    const i = Math.max(PLAN_LADDER.indexOf(min), PLAN_LADDER.indexOf("pro"));
    return PLANS[PLAN_LADDER[Math.min(i, PLAN_LADDER.length - 1)]];
  };

  // A cap on the current plan: the next plan up.
  if (need.above) {
    const i = PLAN_LADDER.indexOf(planFor(tier).slug);
    const next = PLAN_LADDER[Math.min(Math.max(i + 1, 1), PLAN_LADDER.length - 1)];
    const plan = PLANS[next];
    if (plan.slug === planFor(tier).slug) return null; // top of the ladder already
    return paid ? { kind: "portal", plan } : { kind: "plan", plan };
  }

  // A feature on a higher plan.
  if (need.plan && !tierAtLeast(tier, need.plan)) {
    const plan = higher(need.plan);
    return paid ? { kind: "portal", plan } : { kind: "plan", plan };
  }

  if (need.credits != null || need.out) {
    const have = need.have ?? bal?.spendable ?? 0;
    const want = need.credits ?? have + 1;
    const shortfall = Math.max(1, want - have);
    if (paid) {
      if (!packs.length) return null;
      const pack = packs.find((p) => p.credit_count >= shortfall) || packs[packs.length - 1];
      return { kind: "pack", pack, priceLabel: formatCents(pack.price_usd_cents) };
    }
    // Free or trial: pack credits would be frozen, so the plan is the answer.
    const plan = PLAN_LADDER.map((s) => PLANS[s]).find((p) => p.slug !== "free" && p.monthlyCredits >= want)
      || PLANS.pro;
    return { kind: "plan", plan };
  }
  return null;
}

/** "This needs 50 credits; you have 0" / "Scans on more platforms are on Pro". */
export function needSentence(need: UpgradeNeed, bal: BalanceSnapshot | null): string {
  if (need.plan && !tierAtLeast(bal?.tier, need.plan)) {
    return need.reason || `This is on the ${planFor(need.plan).name} plan; you are on ${planFor(bal?.tier).name}.`;
  }
  if (need.credits != null) {
    const have = need.have ?? bal?.spendable ?? 0;
    const cr = (n: number) => `${n} credit${n === 1 ? "" : "s"}`;
    return `This needs ${cr(need.credits)}; you have ${have}.`;
  }
  if (need.out) return need.reason || `You do not have enough credits for this; you have ${need.have ?? bal?.spendable ?? 0}.`;
  if (need.above) return need.reason || `You have reached the limit on the ${planFor(bal?.tier).name} plan.`;
  return need.reason || "This is not included in your plan.";
}

export function offerLabel(o: UpgradeOffer): string {
  if (o.kind === "pack") return `Buy ${o.pack.credit_count} credits for ${o.priceLabel}`;
  if (o.kind === "plan") return `Upgrade to ${o.plan.name}, ${o.plan.monthlyPrice} a month`;
  return `Switch to ${o.plan.name}`;
}
export function offerTip(o: UpgradeOffer): string {
  if (o.kind === "pack") return `Opens a secure checkout in a new tab for ${o.pack.credit_count} credits. Pack credits roll over month to month.`;
  if (o.kind === "plan") return `Opens a secure checkout in a new tab for the ${o.plan.name} plan: ${o.plan.monthlyCredits} credits every month.`;
  return `Opens a page in a new tab where you confirm moving your current subscription to ${o.plan.name}. You see the adjusted amount before anything changes, and you are not charged for a second plan.`;
}

/**
 * Save the resume record, open a tab synchronously (before any await, or the
 * popup blocker eats it), then point it at Checkout. The page the user is on
 * does not move.
 */
export async function startUpgradeCheckout(o: UpgradeOffer, req: UpgradeMomentRequest, bal?: BalanceSnapshot | null): Promise<void> {
  const returnTo = req.returnTo || currentReturnTo();
  const tab = window.open("", "_blank");
  if (req.resume) {
    saveResume({
      action: req.resume.action,
      ref: req.resume.ref ?? null,
      limitType: req.limitType,
      returnTo,
      need: {
        // Unknown cost: "more than they had when they were stopped".
        credits: req.need.credits ?? (req.need.out ? (req.need.have ?? bal?.spendable ?? 0) + 1 : undefined),
        plan: o.kind === "pack" ? undefined : (req.need.plan || o.plan.slug),
      },
    });
  }
  try {
    let url: string | undefined;
    let isSwitch = o.kind === "portal";
    if (o.kind === "portal") {
      // T290: a subscriber moves their subscription to the plan (billing portal switch page),
      // and comes back to returnTo. Never a second subscription.
      const s = await switchPlanUrl(o.plan.slug, returnTo);
      if (s.current) throw new Error(`You are already on ${o.plan.name}.`);
      url = s.url ?? undefined;
    } else if (o.kind === "plan") {
      const resumeMeta = {
        returnTo,
        limit_type: req.limitType,
        resume_action: req.resume?.action ?? null,
        resume_ref: req.resume?.ref ?? null,
      };
      // T290: planUrl turns the server's "already subscribed" answer into the switch.
      const r = await planUrl({ plan: o.plan.slug, ...resumeMeta });
      if (r.current) throw new Error(`You are already on ${o.plan.name}.`);
      isSwitch = r.switched;
      url = r.url ?? undefined;
    } else {
      const resumeMeta = {
        returnTo,
        limit_type: req.limitType,
        resume_action: req.resume?.action ?? null,
        resume_ref: req.resume?.ref ?? null,
      };
      // Credit packs are one-time payments: never blocked for a subscriber.
      const body = { bundle: "credit_pack", sku: o.pack.sku, ...resumeMeta };
      const { data, error } = await supabase.functions.invoke("create-checkout", { body });
      if (error) throw new Error(error.message);
      if (!data?.url) throw new Error(data?.error || "Checkout could not be opened.");
      url = data.url;
    }
    if (!url) throw new Error("Checkout could not be opened.");
    // Google Ads Begin checkout (T258): a new Stripe checkout only, not the billing portal.
    if (!isSwitch) await adsBeginCheckout();
    if (tab) tab.location.href = url;
    else window.open(url, "_blank", "noopener");
  } catch (e) {
    if (tab) tab.close();
    throw e;
  }
}

/** Has the purchase landed for this record? */
export function needMet(rec: Pick<ResumeRecord, "need">, bal: BalanceSnapshot | null): boolean {
  if (!bal) return false;
  if (rec.need.plan && !tierAtLeast(bal.tier, rec.need.plan)) return false;
  if (rec.need.credits != null && bal.spendable < rec.need.credits) return false;
  return true;
}

// ── from an edge-function error to an upgrade moment ────────────────
export interface FromErrorContext {
  attempting: string;
  limitType?: LimitType;
  /** The action's cost when the caller knows it. */
  credits?: number;
  /** The plan that unlocks it when the caller knows it. */
  plan?: PlanSlug;
  reason?: string;
  resume?: ResumeSpec;
}

/** T194: the hover text on the scan-cap dialog. */
export const SCAN_CAP_TIP = "Scans use no credits. To keep them free, Free and Trial plans include 3 scans a day and 20 a month. The daily count restarts at midnight UTC and the monthly count on the 1st (UTC). Paid plans are not limited this way.";

const CREDIT_ERRORS = new Set(["out_of_credits", "insufficient_credits", "quota_exceeded", "budget_exceeded"]);
const PLAN_ERRORS = new Set(["free_tier_blocked", "scans_pro_only", "trial_ended", "tier_required", "persona_required"]);

function num(v: unknown): number | undefined {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
}

async function readErrorBody(error: unknown, data: unknown): Promise<{ body: Record<string, any> | null; status?: number }> {
  if (data && typeof data === "object" && ((data as any).error || (data as any).status || (data as any).reason || (data as any).paywall)) {
    return { body: data as Record<string, any> };
  }
  const ctx = (error as any)?.context;
  const status = typeof ctx?.status === "number" ? ctx.status : undefined;
  if (ctx && typeof ctx.clone === "function") {
    try { return { body: await ctx.clone().json(), status }; } catch { /* not JSON */ }
  }
  return { body: null, status };
}

/**
 * Turn a blocked edge-function answer into an upgrade moment. Returns true when
 * it opened one (the caller then stops: no raw error toast), false when the
 * error is something else. Works with `supabase.functions.invoke`'s
 * { error, data } and with a plain parsed body (pass it as data).
 */
export async function upgradeMomentFromError(error: unknown, data: unknown, ctx: FromErrorContext): Promise<boolean> {
  const { body, status } = await readErrorBody(error, data);
  const b = body || {};
  const code = String(b.error || b.status || b.code || "");
  const reason = String(b.reason || "");
  const msg = String(b.message || b.error || (error as any)?.message || "");

  const base = { attempting: ctx.attempting, resume: ctx.resume };
  // T195 (2026-09-28): the free/trial scan cap (create-scan 429). The server's
  // message states the real limit ("Free plans include 3 scans a day and 20 a
  // month. ..."); the answer is the first paid plan, which is uncapped, and the
  // resume record re-runs the scan once it is active.
  if (code === "scan_limit_reached") {
    openUpgradeMoment({ ...base, limitType: ctx.limitType || "scan", need: { plan: ctx.plan ?? FIRST_PAID_PLAN, reason: msg || ctx.reason, tip: SCAN_CAP_TIP } });
    return true;
  }
  const creditBlock = CREDIT_ERRORS.has(code) || b.status === "insufficient_credits" || reason === "quota_exceeded"
    || /insufficient credits|not enough credits|out of credits/i.test(msg)
    || (status === 402 && !b.error && !b.status);
  if (creditBlock) {
    const credits = ctx.credits ?? num(b.required ?? b.cost ?? b.needed ?? b.credits_required ?? b.credits_needed);
    const have = num(b.available ?? b.balance ?? b.have ?? b.remaining ?? b.credits_available);
    openUpgradeMoment({ ...base, limitType: ctx.limitType || "credits", need: { credits, have, out: credits == null, reason: ctx.reason } });
    return true;
  }
  if (PLAN_ERRORS.has(code) || reason === "tier_too_low" || b.paywall === true || /requires?_?(pro|solo)|_requires_/i.test(code)) {
    openUpgradeMoment({ ...base, limitType: ctx.limitType || "plan", need: { plan: ctx.plan ?? FIRST_PAID_PLAN, reason: ctx.reason } });
    return true;
  }
  if (reason === "cap_reached" || code === "watchlist_limit_reached" || /monthly scan limit reached/i.test(msg)) {
    openUpgradeMoment({ ...base, limitType: ctx.limitType || "plan", need: { above: true, reason: ctx.reason } });
    return true;
  }
  return false;
}

/** The vetter pages' shared request: snapshot or full report, re-run by tier id. */
export function vetterUpgradeRequest(
  isVetting: boolean,
  tierId: string,
  extra?: Pick<UpgradeMomentRequest, "snapshot" | "reportOrder" | "need">,
): UpgradeMomentRequest {
  return {
    limitType: isVetting ? "report" : "dossier",
    attempting: isVetting ? "Open the full report" : "Generate this snapshot",
    need: extra?.need || { out: true },
    resume: { action: "vetter", ref: tierId },
    ...(extra?.snapshot ? { snapshot: extra.snapshot } : {}),
    ...(extra?.reportOrder ? { reportOrder: extra.reportOrder } : {}),
  };
}
